import { useApp, useBehavior, useInput, useTriggerAction, useUpdateWidgetValue } from '@libs/hooks';
import { ActionActionTypeEnum, TWidgetProps } from '@libs/types';
import { ConditionalRender } from '@libs/utils';
import clsx from 'clsx';
import React, { Activity, useEffect, useEffectEvent, useRef, useState } from 'react';
import { TInputPasscodeData } from '../../types/data';
import { TPasscodeOption } from '../../types/options';
import { isIphone } from '../../utils/helpers';
import { ButtonRowView } from '../Buttons/ButtonRowView';
import inputWrapperStyles from '../UI/Wrapper/inputWrapper.module.css';
import styles from './inputPasscode.module.css';
import PasscodeDot from './PasscodeDot';
import { authenticateAndDecryptPasscode, isPasscodeStored, registerAndEncryptPasscode } from './webauthn';

const InputPasscode = ({ widget }: TWidgetProps<TInputPasscodeData, TPasscodeOption>) => {
    const { isHidden, isDisabled } = useBehavior(widget);
    const { triggerActionsByType } = useTriggerAction(widget);
    const { setModal } = useApp();
    const { value, errorMessage } = useInput(widget, { isHidden, isDisabled, triggerActionsByType, setModal });
    const { updateWidgetValue } = useUpdateWidgetValue(widget);

    const passcodeRef = useRef<HTMLInputElement | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const biometryCancelledRef = useRef<boolean>(false);

    const options = widget.options ?? {};
    const useBiometry = options.useBiometry ?? false;
    const isSetPasscode = options.isSetPasscode ?? false;
    const maxLength = options.maxLength ?? 4;

    const [touched, setTouched] = useState(false);
    const [prevIsError, setPrevIsError] = useState(!!options.isError);

    if (!!options.isError !== prevIsError) {
        setPrevIsError(!!options.isError);
        if (options.isError) {
            setTouched(false);
        }
    }

    const isError = !!options.isError && !touched;

    const updateValueForPassCode = (value: string, allowTriggerAction?: boolean) => {
        updateWidgetValue(value);
        if (allowTriggerAction && value?.length === maxLength) {
            triggerActionsByType(ActionActionTypeEnum.onFilled)
                .then()
                .catch((e) => console.error(e));
        }
    };

    const triggerCheckWebAuth = () => {
        authenticateAndDecryptPasscode()
            .then((passcode) => updateValueForPassCode(passcode ?? '', true))
            .catch((e) => {
                if (e instanceof Error && e.name === 'NotAllowedError') {
                    biometryCancelledRef.current = true;
                } else {
                    console.error(e);
                }
            });
    };

    const checkPassCodeInWebAuth = useEffectEvent(() => {
        if (useBiometry && isPasscodeStored()) {
            triggerCheckWebAuth();
        }
    });

    useEffect(() => {
        if (options.isError) {
            updateWidgetValue('');
        } else {
            checkPassCodeInWebAuth();
        }
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [updateWidgetValue, options.isError]);

    const handleBlur = (_e: React.FocusEvent<HTMLInputElement>) => {
        if (value?.length < maxLength) {
            timerRef.current = setTimeout(() => {
                passcodeRef.current?.focus();
            }, 100); // Small delay helps in Safari
        }
    };

    const handleClickOverlay = () => {
        passcodeRef.current?.focus();
    };

    const handleChangeLocal = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!touched) {
            setTouched(true);
        }
        const value = e?.target?.value;

        updateValueForPassCode(value);
        if (value?.length === maxLength) {
            if (isSetPasscode && !biometryCancelledRef.current) {
                registerAndEncryptPasscode(value)
                    .catch((e) => console.error(e))
                    .finally(() => {
                        updateValueForPassCode(value, true);
                    });
            } else {
                updateValueForPassCode(value, true);
            }
        }
    };

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <input
                className={styles.passCodeInput}
                data-test={widget.uid + '_input'}
                ref={passcodeRef}
                value={value || ''}
                onBlur={handleBlur}
                onChange={handleChangeLocal}
                maxLength={maxLength}
                autoComplete="off"
                type="tel"
                inputMode="numeric"
                autoFocus={!isIphone()}
                tabIndex={widget.data?.tabIndex}
            />
            <div data-test={widget.uid} className={styles.dotsContainer}>
                {[...Array(maxLength)].map((_value: undefined, index: number) => (
                    <PasscodeDot key={index} currentLength={value?.length} dotIndex={index + 1} />
                ))}
            </div>
            <ConditionalRender condition={isError && !!errorMessage}>
                <div
                    data-test={widget.uid + '_errorText'}
                    className={clsx(
                        styles.hintWrapper,
                        inputWrapperStyles.hintContainer,
                        isError && inputWrapperStyles.errorColor,
                        inputWrapperStyles.alignCenter,
                    )}
                >
                    {errorMessage}
                </div>
            </ConditionalRender>
            <ConditionalRender condition={useBiometry && isPasscodeStored()}>
                <div className={styles.biometryButton}>
                    <ButtonRowView
                        uid={widget.uid + '_useBiometry'}
                        iconName={'ic_face_id'}
                        label={widget.data?.messages?.useBothBiometryTitle}
                        alignment={'center'}
                        hierarchy={'primary'}
                        onClick={() => {
                            triggerCheckWebAuth();
                        }}
                    />
                </div>
            </ConditionalRender>
            <div data-test={widget.uid + '_overlay'} className={styles.passCodeOverlay} onClick={handleClickOverlay} />
        </Activity>
    );
};

export { InputPasscode };
